/**
 * The story (ui/DESIGN.md §5): the stage dims, the map stays, and the thing's journey plays hop by hop
 * with a comic panel anchored to the system the document is in. The host page renders <Story> inside the
 * stage (its bottom bar sticks to the bottom of the viewport there) and passes a ref to the map's own box,
 * where the moving documents, the lit flow, the rings and the panel are drawn so that MapGeometry's
 * coordinates are the overlay's. While open, `data-story` sits on <html> (see story.css).
 */
import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState, type ChangeEvent, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';
import { Api, type Message } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { count, duration } from '../lib/format.ts';
import { useEdgeFade } from '../stage/EdgeFade.ts';
import { CloseIcon } from '../components/icons.tsx';
import { IconButton, NounTag, Surface, SystemBadge, cx, glyphPaths, noun } from '../design/primitives.tsx';
import type { MapGeometry, MapNode } from '../map/types.ts';
import { dayWord } from '../stage/answer.ts';
import { fieldLabel, isIdentifier } from '../peek/fields.ts';
import { LABEL_W } from '../map/layout.ts';
import { clamp, contextOf, panelsOf, phasesOf, sceneOf, type DocRef, type Journey, type Panel, type Phase, type PlayDoc, type Scene, type StoryFlow, type Token, type Tone } from './engine.ts';
import './story.css';

export interface StoryProps {
  journey: Journey;
  /** The map's flows (MapFlow[] is fine): which integration moves what where, and its rhythm. */
  flows: StoryFlow[];
  /** The map's nodes: systems and formats of the data models. */
  nodes: MapNode[];
  /** The rendered map's geometry (from Map's geometryRef); null until the map is laid out. */
  geometry: MapGeometry | null;
  /** The map's own box (marked .stage-map): the theatre is drawn inside it, in its coordinates. */
  mapRef: RefObject<HTMLElement | null>;
  /** Story speed, ⅛×–8×; the page shares it with the map so both move together. */
  speed: number;
  onSpeed: (speed: number) => void;
  /** A document in a panel was chosen: open it (a document peek). */
  onMessage?: (ref: DocRef) => void;
  onClose: () => void;
  /** How a panel gets a document's fields; Api.message of the tenant unless the page says otherwise. */
  loadDoc?: (ref: DocRef) => Promise<Message>;
}

/** The glyph on the stage is 1.45× the one in a list. */
const GLYPH = 1.45;
/** What the shell above and the bar below take of the viewport, so nothing told is left under them. */
const SHELL_ROOM = 68;
const BAR_ROOM = 140;
/** Where the map's top goes when the story opens (under the shell), as story.css's scroll-margin says. */
const MAP_TOP = 80;
const len = (p: Phase) => p.anim + p.hold;
/** Where the bar's top is on screen (it is not always stuck to the bottom): nothing told goes under it. */
const barTop = (bar: RefObject<HTMLDivElement | null>) => (bar.current ? bar.current.getBoundingClientRect().top - 12 : window.innerHeight - BAR_ROOM);
/** Where the map's drawing is on screen: MapGeometry's origin (the map may pad or scroll its drawing), else the box itself. */
const originOf = (geometry: MapGeometry, mapEl: HTMLElement): DOMRect => geometry.origin?.() ?? mapEl.getBoundingClientRect();
const reducedMotion = () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export function Story({ journey, flows, nodes, geometry, mapRef, speed, onSpeed, onMessage, onClose, loadDoc }: StoryProps) {
  const { tenant } = useAuth();
  const ctx = useMemo(() => contextOf(nodes, flows), [nodes, flows]);
  const phases = useMemo(() => phasesOf(journey.docs, flows, true, ctx.system), [journey, flows, ctx]);
  const panels = useMemo(() => panelsOf(phases, ctx), [phases, ctx]);
  const starts = useMemo(() => phases.reduce<number[]>((a, _p, i) => [...a, i === 0 ? 0 : a[i - 1] + len(phases[i - 1])], []), [phases]);
  const total = useMemo(() => phases.reduce((n, p) => n + len(p), 0), [phases]);
  const reduced = reducedMotion();

  // Story time, in seconds of animation; the clock and the documents follow it
  const [t, setT] = useState(0);
  const tRef = useRef(0);
  const [playing, setPlaying] = useState(true);
  const speedRef = useRef(speed);
  speedRef.current = speed;
  const jump = (x: number) => {
    tRef.current = x;
    setT(x);
  };
  useEffect(() => {
    jump(0);
    setPlaying(true);
  }, [journey]);
  useEffect(() => {
    if (!playing || speed <= 0) return;
    let raf = 0;
    let last: number | null = null;
    const tick = (now: number) => {
      const dt = last === null ? 0 : (now - last) / 1000;
      last = now;
      let n = Math.min(total, tRef.current + dt * speedRef.current);
      if (reduced) {
        // No motion: straight to the stop of the phase we are in
        const k = Math.max(0, starts.findIndex((s, i) => n >= s && n < s + len(phases[i])));
        const ph = phases[k];
        if (ph && ph.hold > 0 && n - starts[k] < ph.anim) n = starts[k] + ph.anim;
      }
      jump(n);
      if (n >= total) setPlaying(false);
      else raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, speed, total, starts, phases, reduced]);

  // The page recedes (story.css), the map comes forward and into view
  useLayoutEffect(() => {
    document.documentElement.setAttribute('data-story', '');
    return () => document.documentElement.removeAttribute('data-story');
  }, []);
  const [mapEl, setMapEl] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    setMapEl(mapRef.current);
  }, [mapRef, geometry]);
  // The map moves up under the shell so the panels have the room; a spacer after the bar lets the page
  // scroll that far when there is nothing else below (the bar stays stuck to the bottom meanwhile)
  const barRef = useRef<HTMLDivElement>(null);
  const spacerRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!mapEl) return;
    // Sized in the DOM, not in state, so the scroll below finds the room there already
    const fit = () => {
      const bar = barRef.current?.offsetHeight ?? 110;
      if (spacerRef.current) spacerRef.current.style.height = `${Math.max(0, window.innerHeight - MAP_TOP - mapEl.offsetHeight - 16 - bar - 16)}px`;
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(mapEl);
    window.addEventListener('resize', fit);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', fit);
    };
  }, [mapEl]);
  useEffect(() => {
    mapEl?.scrollIntoView({ block: 'start', behavior: reduced ? 'auto' : 'smooth' });
  }, [mapEl]); // eslint-disable-line react-hooks/exhaustive-deps

  let i = starts.findIndex((s, k) => t >= s && t < s + len(phases[k]));
  if (i < 0) i = phases.length - 1;
  const ph = phases[i];
  // The node a stop is about is never left under the bar or the shell: the page scrolls just enough
  const anchor = panels[i]?.anchor ?? null;
  // Where the document goes next: the panel keeps off that node and prefers the side away from it
  const next = panels.slice(i + 1).find((x) => x.anchor && x.anchor !== anchor)?.anchor ?? null;
  useEffect(() => {
    const b = anchor && geometry ? geometry.box(anchor) : null;
    if (!b || !mapEl || !geometry) return;
    const r = originOf(geometry, mapEl);
    const top = r.top + b.y;
    const bottom = top + b.h;
    const over = bottom - barTop(barRef);
    const under = SHELL_ROOM - top;
    const by = over > 0 ? over : under > 0 ? -under : 0;
    if (by) window.scrollBy({ top: by, behavior: reduced ? 'auto' : 'smooth' });
  }, [anchor, geometry, mapEl, reduced]);
  /** Straight to where a stop is told (the hold), or halfway into a wait. */
  const stopAt = (k: number) => {
    const j = clamp(k, 0, phases.length - 1);
    const x = phases[j];
    jump(starts[j] + (x.hold > 0 ? x.anim + 0.001 : Math.min(x.anim / 2, 0.5)));
    setPlaying(true);
  };
  const togglePlay = () => {
    if (speed <= 0) onSpeed(1);
    if (t >= total) {
      jump(0);
      setPlaying(true);
    } else setPlaying((x) => !x);
  };
  // ←/→ prev and next stop, space play/pause, Esc close; the handlers of the latest render, registered once
  const keys = useRef({ prev: () => undefined as void, next: () => undefined as void, toggle: () => undefined as void, close: onClose });
  keys.current = { prev: () => stopAt(i - 1), next: () => stopAt(i + 1), toggle: togglePlay, close: onClose };
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const typing = !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
      if (e.key === 'Escape') keys.current.close();
      else if (e.key === 'ArrowRight' && !typing) keys.current.next();
      else if (e.key === 'ArrowLeft' && !typing) keys.current.prev();
      else if (e.key === ' ' && !typing && el?.tagName !== 'BUTTON') {
        e.preventDefault();
        keys.current.toggle();
      } else return;
      e.stopPropagation();
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, []);

  const load = useMemo(() => loadDoc ?? ((r: DocRef) => Api.message(tenant?.id ?? '', r.vaultId, r.uuid, r.day)), [loadDoc, tenant?.id]);
  const nodeIds = useMemo(() => nodes.map((n) => n.id), [nodes]);
  const obstacles = useMemo(() => (geometry ? obstaclesOf(geometry, nodeIds, flows) : null), [geometry, nodeIds, flows]);
  const span = useMemo(() => {
    const ats = journey.docs.map((d) => d.at);
    return ats.length ? { first: Math.min(...ats), last: Math.max(...ats) } : null;
  }, [journey]);
  const title = <Title journey={journey} stop={i + 1} stops={phases.length} span={span} />;

  if (!ph) {
    return (
      <Bar ref={barRef} title={title} onClose={onClose}>
        <span className="text-small text-n8 px-2">Nothing to tell: {journey.title} has no documents with runs.</span>
      </Bar>
    );
  }
  const p = ph.anim > 0 ? clamp((t - starts[i]) / ph.anim) : 1;
  const holding = t - starts[i] >= ph.anim;
  const clock = ph.kind === 'start' || ph.kind === 'end' ? ph.at : ph.kind === 'wait' ? ph.from + (ph.to - ph.from) * p : ph.start + (ph.end - ph.start) * p;
  // A story tells at every stop from the moment the document is there until it leaves; while it is on its way
  // the stop before stays, faded, so the stage is never a map with a toolbar
  const arrived = holding || ph.kind !== 'run';
  const panel = arrived ? panels[i] : null;
  const before = !arrived && i > 0 ? panels[i - 1] : null;
  const scene = geometry ? sceneOf(ph, reduced ? 1 : p, holding, geometry, ctx) : null;
  const paused = !playing || speed <= 0;

  return (
    <>
      {mapEl && geometry && scene && obstacles && createPortal(
        <Theatre scene={scene} panel={panel} before={before} next={next} goingTo={panels[i].anchor} index={i} total={phases.length} geometry={geometry} mapEl={mapEl} barRef={barRef} nodeIds={nodeIds} obstacles={obstacles} load={load} onMessage={onMessage} />,
        mapEl,
      )}
      <div ref={spacerRef} aria-hidden="true" />
      <Bar ref={barRef} title={title} onClose={onClose}
        nav={(
          <>
            <IconButton label="Previous stop" onClick={() => stopAt(i - 1)} disabled={i === 0} className="disabled:opacity-30 disabled:cursor-default">
              <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M9 2 4 7l5 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </IconButton>
            <button type="button" aria-label={paused ? 'Play' : 'Pause'} aria-pressed={!paused} onClick={togglePlay}
              className="size-10 rounded-full grid place-items-center bg-n10 text-n1 border-0 cursor-pointer transition-transform duration-[var(--t-micro)] ease-mv active:scale-95">
              {paused || t >= total ? (
                <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M3.5 2v10l8.5-5z" fill="currentColor" /></svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M3.5 2.5h2.5v9H3.5zM8 2.5h2.5v9H8z" fill="currentColor" /></svg>
              )}
            </button>
            <IconButton label="Next stop" onClick={() => stopAt(i + 1)} disabled={i >= phases.length - 1} className="disabled:opacity-30 disabled:cursor-default">
              <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="m5 2 5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </IconButton>
          </>
        )}
        right={(
          <>
            <Speed speed={speed} onSpeed={onSpeed} />
            <span className="font-mono text-body font-medium tabular-nums text-n10 whitespace-nowrap text-right">{dayjs(clock).format('ddd D MMM HH:mm:ss')}</span>
          </>
        )}>
        <Strip panels={panels} current={i} onPick={stopAt} />
      </Bar>
    </>
  );
}

/** "The story of sales order SO-763643 · stop 7 of 16 · Wed 13:17 → Wed 21:19", with the noun it is a story of. */
function Title({ journey, stop, stops, span }: { journey: Journey; stop: number; stops: number; span: { first: number; last: number } | null }) {
  return (
    <>
      <NounTag kind={journey.kind} />
      <span className="text-small font-medium text-n10 truncate"><Said text={journey.title} /></span>
      {stops > 0 && (
        <span className="flex items-baseline gap-1.5 text-small text-n7 whitespace-nowrap">
          <span className="text-n6">·</span>
          <span className="tabular-nums">stop {count(stop)} of {count(stops)}</span>
          {span && (
            <>
              <span className="text-n6">·</span>
              <span className="font-mono tabular-nums">{dayWord(span.first, Date.now())} {dayjs(span.first).format('HH:mm')} → {dayWord(span.last, Date.now()) === dayWord(span.first, Date.now()) ? '' : `${dayWord(span.last, Date.now())} `}{dayjs(span.last).format('HH:mm')}</span>
            </>
          )}
        </span>
      )}
    </>
  );
}

// ---------------------------------------------------------------- over the map

/** Re-renders on scroll and resize, so what is placed against the viewport follows it. */
function useViewportTick(): number {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const move = () => setTick((x) => x + 1);
    window.addEventListener('scroll', move, true);
    window.addEventListener('resize', move);
    return () => {
      window.removeEventListener('scroll', move, true);
      window.removeEventListener('resize', move);
    };
  }, []);
  return tick;
}

const Theatre = memo(function Theatre({ scene, panel, before, next, goingTo, index, total, geometry, mapEl, barRef, nodeIds, obstacles, load, onMessage }: {
  scene: Scene; panel: Panel | null;
  /** The stop before, shown faded while the document is on its way from it. */
  before: Panel | null;
  next: string | null;
  /** Where the document is going now: the faded panel keeps off that node. */
  goingTo: string | null;
  index: number; total: number; geometry: MapGeometry; mapEl: HTMLElement; barRef: RefObject<HTMLDivElement | null>; nodeIds: string[];
  obstacles: Obstacles; load: (r: DocRef) => Promise<Message>; onMessage?: (ref: DocRef) => void;
}) {
  // The sticker is slapped on the document itself where there is one; at the end, with nothing on the map, on the panel
  const lead = scene.tokens.at(-1) ?? null;
  const [stickerBox, setStickerBox] = useState<Box | null>(null);
  // The map's time labels near a ringed node step aside: the ring and the moving document must read clean
  const ringed = scene.rings.map((r) => r.id).join('|');
  useEffect(() => {
    const boxes = ringed.split('|').filter(Boolean).map((id) => geometry.box(id)).filter((b): b is Box => !!b)
      .map((b) => ({ x: b.x - RING, y: b.y - RING, w: b.w + 2 * RING, h: b.h + 2 * RING }));
    const hidden: Element[] = [];
    for (const el of mapEl.querySelectorAll<SVGTextElement>('text.mvm-label')) {
      const bb = el.getBBox();
      if (boxes.some((b) => overlap({ x: bb.x, y: bb.y, w: bb.width, h: bb.height }, b) > 0)) {
        el.setAttribute('data-story-aside', '');
        hidden.push(el);
      }
    }
    return () => hidden.forEach((el) => el.removeAttribute('data-story-aside'));
  }, [ringed, geometry, mapEl]);
  // The overlay sits exactly on the map's drawing, in its coordinates
  const o = originOf(geometry, mapEl);
  const r = mapEl.getBoundingClientRect();
  return (
    <div className="absolute z-10 pointer-events-none overflow-visible" aria-live="polite" style={{ left: o.left - r.left, top: o.top - r.top, width: o.width, height: o.height }}>
      <svg className="absolute inset-0 overflow-visible" width="100%" height="100%" aria-hidden="true">
        {scene.lit.map((l, k) => (
          <path key={k} d={l.d} fill="none" stroke={l.failed ? 'var(--fail)' : 'var(--accent)'} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" opacity={0.9} />
        ))}
        {scene.rings.map((r) => {
          const b = geometry.box(r.id);
          return b && <rect key={r.id} x={b.x - 4} y={b.y - 4} width={b.w + 8} height={b.h + 8} rx={Math.min(22, b.h / 2 + 4)} fill="none" stroke={r.failed ? 'var(--fail)' : 'var(--accent)'} strokeWidth={2} />;
        })}
        {scene.tokens.map((k, j) => <Doc key={j} token={k} />)}
      </svg>
      {scene.label && (
        <span className="absolute -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-full bg-n10 text-n1 px-2.5 py-1 font-mono text-micro tracking-wide tabular-nums"
          style={{ left: scene.label.x, top: scene.label.y }}>{scene.label.text}</span>
      )}
      {panel && lead && <Sticker key={`sticker-${index}`} text={panel.sticker} tone={panel.tone} at={lead} label={scene.label} lit={scene.lit} obstacles={obstacles} mapW={o.width} onPlace={setStickerBox} />}
      {/* one list, keyed by stop, so the panel of the stop before stays the same element as it fades */}
      {[
        before && (
          <ComicPanel key={`panel-${index - 1}`} panel={before} next={goingTo} n={index} total={total} sticker={false} avoid={null} faded
            geometry={geometry} mapEl={mapEl} barRef={barRef} nodeIds={nodeIds} obstacles={obstacles} load={load} onMessage={onMessage} />
        ),
        panel && (
          <ComicPanel key={`panel-${index}`} panel={panel} next={next} n={index + 1} total={total} sticker={!lead} avoid={lead ? stickerBox : null}
            geometry={geometry} mapEl={mapEl} barRef={barRef} nodeIds={nodeIds} obstacles={obstacles} load={load} onMessage={onMessage} />
        ),
      ]}
    </div>
  );
});

// ---------------------------------------------------------------- the sticker

type Pt = { x: number; y: number };
/** What a sticker keeps off: the nodes (with their rings) and points along every arrow of the map. */
interface Obstacles { nodes: Box[]; paths: { d: string; pts: Pt[] }[] }
const RING = 6;
const SAMPLES = 40;

function obstaclesOf(geo: MapGeometry, nodeIds: string[], flows: StoryFlow[]): Obstacles {
  const nodes = nodeIds.map((id) => geo.box(id)).filter((b): b is Box => !!b).map((b) => ({ x: b.x - RING, y: b.y - RING, w: b.w + 2 * RING, h: b.h + 2 * RING }));
  const seen = new Set<string>();
  const paths: Obstacles['paths'] = [];
  for (const f of flows) {
    const rf = geo.flowBetween(f.from, f.to, f.process);
    const p = rf ? geo.path(rf.id) : null;
    if (!rf || !p || seen.has(p.d)) continue;
    seen.add(p.d);
    paths.push({ d: p.d, pts: Array.from({ length: SAMPLES + 1 }, (_, k) => p.pointAt(k / SAMPLES)) });
  }
  return { nodes, paths };
}

/** The glyph's half height on the stage, and how far above its path the sticker sits. */
const GLYPH_HALF = 10 * GLYPH;
const STICKER_GAP = 12;
const inside = (p: Pt, b: Box, m = 0) => p.x >= b.x - m && p.x <= b.x + b.w + m && p.y >= b.y - m && p.y <= b.y + b.h + m;

/**
 * Where the sticker goes: over the document, 12 px above its path; when that would cover a node or another
 * arrow, above the node the document sits at, beside the document or under it — whichever covers the least,
 * never past the map's sides.
 */
function placeSticker(at: Pt, w: number, h: number, obs: Obstacles, lit: string[], label: Box | null, mapW: number): Pt {
  const near = obs.nodes.find((b) => inside(at, b, 2)) ?? null;
  const above = at.y - GLYPH_HALF - STICKER_GAP - h;
  const below = at.y + GLYPH_HALF + STICKER_GAP;
  // A document at rest sits on a node's edge: the sticker hangs off that edge, not over the node
  const cands: Pt[] = [
    { x: at.x - w / 2, y: above },
    { x: at.x + 2, y: above },
    { x: at.x - 2 - w, y: above },
    ...(near ? [{ x: at.x - w / 2, y: near.y - STICKER_GAP - h }] : []),
    { x: at.x + GLYPH_HALF + STICKER_GAP, y: at.y - h / 2 },
    { x: at.x - GLYPH_HALF - STICKER_GAP - w, y: at.y - h / 2 },
    { x: at.x - w / 2, y: below },
    { x: at.x + 2, y: below },
    { x: at.x - 2 - w, y: below },
  ];
  const own = new Set(lit);
  const cost = (c: Pt, k: number) => {
    const box = { x: c.x, y: c.y, w, h };
    let n = k * 8 + obs.nodes.reduce((sum, b) => sum + overlap(box, b), 0);
    if (label) n += overlap(box, label) * 2;
    for (const p of obs.paths) if (!own.has(p.d)) n += 300 * p.pts.filter((q) => inside(q, box, 4)).length;
    n += 50 * (Math.max(0, -c.x) + Math.max(0, c.x + w - mapW));
    // the map's card has room above the drawing; beyond it the sticker would sit on the page
    n += 20 * Math.max(0, -16 - c.y);
    return n;
  };
  const costs = cands.map(cost);
  return cands[costs.indexOf(Math.min(...costs))];
}

/** The sticker on the stage, at the document: pops at every stop (mv-sticker), then follows the document. */
function Sticker({ text, tone, at, label, lit, obstacles, mapW, onPlace }: {
  text: string; tone: Tone; at: Pt; label: Scene['label']; lit: Scene['lit']; obstacles: Obstacles; mapW: number;
  /** Where it ended up, for the panel to keep off. */
  onPlace: (box: Box) => void;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [size, setSize] = useState({ w: text.length * 9 + 28, h: 30 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setSize({ w: el.offsetWidth, h: el.offsetHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text]);
  // The counting "waits 15 h" label sits over the waiting document: the sticker keeps off it too
  const labelBox = label ? { x: label.x - (label.text.length * 6.8 + 20) / 2, y: label.y - 22, w: label.text.length * 6.8 + 20, h: 22 } : null;
  const p = placeSticker(at, size.w, size.h, obstacles, lit.map((l) => l.d), labelBox, mapW);
  useLayoutEffect(() => {
    onPlace({ x: p.x, y: p.y, w: size.w, h: size.h });
  }, [p.x, p.y, size.w, size.h, onPlace]);
  return <span ref={ref} className="story-sticker story-sticker-stage mv-sticker" data-tone={tone} style={{ left: p.x, top: p.y }}>{text}</span>;
}

/** A document on the stage: the same page, fold and format mark as everywhere, drawn large. */
function Doc({ token: k }: { token: Token }) {
  const g = glyphPaths(k.format);
  const color = k.failed ? 'var(--fail)' : 'var(--c-doc)';
  return (
    <>
      {k.trail && <circle className="story-trail" cx={k.trail.x} cy={k.trail.y} r={7 * GLYPH} fill={color} opacity={0.16 * k.opacity} />}
      <g className="story-doc" transform={`translate(${k.x},${k.y}) rotate(${k.tilt ?? 0}) scale(${GLYPH * k.scale})`} opacity={k.opacity}>
        <path d={g.page} fill="none" stroke="var(--n2)" strokeWidth={5} strokeLinejoin="round" opacity={0.9} />
        <path d={g.page} fill={k.failed ? 'var(--fail-soft)' : 'var(--n2)'} stroke={color} strokeWidth={1.4} strokeLinejoin="round" />
        <path d={g.fold} fill="none" stroke={color} strokeWidth={1.2} strokeLinejoin="round" />
        <text y={g.markY} textAnchor="middle" fontFamily="var(--mono)" fontSize={g.markSize} fontWeight={700} fill={color}>{g.mark}</text>
        {k.n && k.n > 1 && (
          <g transform="translate(7.5,-9)">
            <circle r={5.5} fill={color} />
            <text y={2.1} textAnchor="middle" fontFamily="var(--mono)" fontSize={5.6} fontWeight={700} fill="var(--n2)">×{k.n}</text>
          </g>
        )}
      </g>
    </>
  );
}

const PANEL_W = 340;
const GAP = 22;
type Side = 'right' | 'left' | 'below' | 'above' | null;
type Box = { x: number; y: number; w: number; h: number };
const overlap = (a: Box, b: Box) => Math.max(0, Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x)) * Math.max(0, Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y));

/** Covering the node the document travels to next weighs this much more than covering any other node. */
const NEXT_WEIGHT = 6;
/** Sitting on the side the document travels to counts as covering a node of this size. */
const TOWARD_COST = 3000;

/**
 * Where the panel goes: beside the node it is about, else below or above it, never over it and never
 * outside the viewport; of the placements that fit, the one covering the least of the other nodes, keeping
 * well off the node the document goes to next and preferring the side away from it (to the right first
 * when equal). The tail points at the node.
 */
function place(b: Box | null, w: number, h: number, v: { left: number; top: number; right: number; bottom: number }, others: Box[], next: Box | null = null, avoid: Box | null = null, own: Pt[][] = []) {
  const fitY = (top: number) => clamp(top, v.top, Math.max(v.top, v.bottom - h));
  const fitX = (left: number) => clamp(left, v.left, Math.max(v.left, v.right - w));
  if (!b) return { left: fitX((v.left + v.right) / 2 - w / 2), top: fitY(v.top + 24), side: null as Side, tail: { x: 0, y: 0 } };
  const cx0 = b.x + b.w / 2;
  const cy = b.y + b.h / 2;
  const candidates: { side: Side; left: number; top: number; fits: boolean }[] = [
    { side: 'right', left: b.x + b.w + GAP, top: fitY(cy - 72), fits: b.x + b.w + GAP + w <= v.right },
    { side: 'left', left: b.x - GAP - w, top: fitY(cy - 72), fits: b.x - GAP - w >= v.left },
    { side: 'below', left: fitX(cx0 - w / 2), top: b.y + b.h + GAP, fits: b.y + b.h + GAP + h <= v.bottom },
    { side: 'above', left: fitX(cx0 - w / 2), top: b.y - GAP - h, fits: b.y - GAP - h >= v.top },
  ];
  const fitting = candidates.filter((c) => c.fits);
  let pick = fitting[0];
  if (fitting.length > 1) {
    const dx = next ? next.x + next.w / 2 - cx0 : 0;
    const dy = next ? next.y + next.h / 2 - cy : 0;
    const toward = (side: Side) => !!next && ((side === 'right' && dx > 0) || (side === 'left' && dx < 0) || (side === 'below' && dy > 0) || (side === 'above' && dy < 0));
    const cost = (c: { side: Side; left: number; top: number }) => {
      const box = { x: c.left, y: c.top, w, h };
      return (b ? overlap(box, b) * 40 : 0) + others.reduce((n, o) => n + overlap(box, o), 0) + (next ? overlap(box, next) * NEXT_WEIGHT : 0) + (avoid ? overlap(box, avoid) * 4 : 0) + own.reduce((m, pts) => m + 400 * pts.filter((q) => inside(q, box, 6)).length, 0) + (toward(c.side) ? TOWARD_COST : 0);
    };
    pick = fitting.reduce((best, c) => (cost(c) < cost(best) ? c : best), fitting[0]);
  }
  if (!pick) {
    // Nowhere fits: beside the node on the roomier side, as far as the viewport allows
    const side: Side = b.x - v.left > v.right - (b.x + b.w) ? 'left' : 'right';
    pick = { side, left: fitX(side === 'left' ? b.x - GAP - w : b.x + b.w + GAP), top: fitY(cy - 72), fits: false };
  }
  const { side, left, top } = pick;
  const tail = side === 'below' || side === 'above'
    ? { x: clamp(cx0 - left - 8, 16, w - 24), y: side === 'below' ? -8 : h - 8 }
    : { x: side === 'left' ? w - 8 : -8, y: clamp(cy - top - 8, 18, h - 26) };
  return { left, top, side, tail };
}

/** A comic panel over the map: anchored to the node the document is in, with a sticker on its corner. */
const ComicPanel = memo(function ComicPanel({ panel, next, n, total, sticker, avoid, faded, geometry, mapEl, barRef, nodeIds, obstacles, load, onMessage }: {
  panel: Panel; next: string | null; n: number; total: number;
  /** Whether the sticker sits on the panel's corner (when nothing is on the stage to carry it). */
  sticker: boolean;
  /** The sticker's box on the stage, which the panel keeps off. */
  avoid: Box | null;
  /** The stop before, kept at half strength while the document is on its way. */
  faded?: boolean;
  geometry: MapGeometry; mapEl: HTMLElement; barRef: RefObject<HTMLDivElement | null>; nodeIds: string[]; obstacles: Obstacles; load: (r: DocRef) => Promise<Message>; onMessage?: (ref: DocRef) => void;
}) {
  useViewportTick();
  const ref = useRef<HTMLDivElement>(null);
  const [h, setH] = useState(240);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setH(el.offsetHeight);
    const ro = new ResizeObserver(() => setH(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // The viewport in the map's coordinates, with room for the shell above and the bar below
  const r = originOf(geometry, mapEl);
  const v = { left: Math.max(-r.left + 12, LABEL_W + 10), top: -r.top + SHELL_ROOM, right: window.innerWidth - r.left - 12, bottom: barTop(barRef) - r.top };
  const b = panel.anchor ? geometry.box(panel.anchor) : null;
  const others = nodeIds.filter((id) => id !== panel.anchor).map((id) => geometry.box(id)).filter((x): x is Box => !!x);
  // the arrows in and out of the anchor node stay uncovered
  const own = b ? obstacles.paths.filter((p) => p.pts.some((q) => inside(q, b, 10))).map((p) => p.pts) : [];
  const { left, top, side, tail } = place(b, PANEL_W, h, v, others, next ? geometry.box(next) : null, avoid, own);
  const tilt = n % 2 ? -1.2 : 1;
  return (
    <div ref={ref} className={cx('story-panel absolute z-20 pointer-events-auto mv-comic', faded && 'is-faded')} style={{ left, top, ['--mv-tilt' as string]: `${tilt}deg` }}>
      <Surface level={2} className="relative p-4 pt-[18px] flex flex-col gap-2.5 overflow-visible">
        {side && <span className="story-tail" style={{ left: tail.x, top: tail.y }} aria-hidden="true" />}
        {sticker && <span className="story-sticker mv-sticker" data-tone={panel.tone}>{panel.sticker}</span>}
        <div className={cx('flex items-center gap-2.5', sticker && 'pr-14')}>
          {panel.system && <SystemBadge code={panel.system} size={24} />}
          {panel.formatIn && (
            <span className="font-mono text-small text-doc whitespace-nowrap">
              {panel.formatIn}{panel.formatOut && panel.formatOut !== panel.formatIn ? ` → ${panel.formatOut}` : ''}
            </span>
          )}
          <span className="ml-auto font-mono text-micro text-n7 tabular-nums">{n} / {total}</span>
        </div>
        <p className="m-0 text-body leading-[1.4] text-n10 font-medium"><Said text={panel.text} /></p>
        {panel.sub && <p className="m-0 text-small text-n8">{panel.sub}</p>}
        <span className="font-mono text-micro tracking-wide text-n7">{panel.when}</span>
        {panel.doc && <DocFields doc={panel.doc} load={load} onOpen={onMessage} />}
      </Surface>
    </div>
  );
});

/** Looks like an identifier (an order number, a file name, a code): set in mono, never broken across lines. */
const IDENT = /^[A-Za-z0-9][A-Za-z0-9_./:-]*\d[A-Za-z0-9_./:-]*$/;

/** The run's own words, with the identifiers in them set in mono. */
function Said({ text }: { text: string }) {
  return (
    <>
      {text.split(/(\s+)/).map((w, k) => {
        const m = /^(.*?)([.,:;)]*)$/.exec(w);
        const core = m?.[1] ?? w;
        return core.length >= 4 && IDENT.test(core)
          ? <span key={k}><span className="font-mono text-small whitespace-nowrap">{core}</span>{m?.[2]}</span>
          : <span key={k}>{w}</span>;
      })}
    </>
  );
}

const docCache = new Map<string, Promise<Message>>();
const PREFERRED = /(id|number|no|nr|reference|code|sku|item|qty|quantity|amount|total|status|name|event)$/i;
const leaf = (p: string) => p.split('/').pop() ?? p;
const CAN_WAIT = 36;

/** A peek at the document's key fields: identifiers, amounts and names before the rest, three at most. */
function DocFields({ doc, load, onOpen }: { doc: PlayDoc; load: (r: DocRef) => Promise<Message>; onOpen?: (ref: DocRef) => void }) {
  const [m, setM] = useState<Message | null | undefined>(undefined);
  useEffect(() => {
    let live = true;
    const k = doc.ref.uuid;
    if (!docCache.has(k)) docCache.set(k, load(doc.ref));
    docCache.get(k)!.then((x) => live && setM(x), () => live && setM(null));
    return () => {
      live = false;
    };
  }, [doc, load]);
  const fields = (m?.fields ?? []).filter(([, v]) => v.trim() && v.length <= CAN_WAIT)
    .sort((x, y) => Number(PREFERRED.test(leaf(y[0]))) - Number(PREFERRED.test(leaf(x[0])))).slice(0, 3);
  const body = (
    <>
      {m === undefined && <><span className="h-3 w-20 rounded mv-shimmer" /><span className="h-3 w-28 rounded mv-shimmer" /><span className="h-3 w-24 rounded mv-shimmer" /><span className="h-3 w-16 rounded mv-shimmer" /><span className="h-3 w-16 rounded mv-shimmer" /><span className="h-3 w-24 rounded mv-shimmer" /></>}
      {m === null && <span className="col-span-2 text-n7">{noun(doc.cls)} · {doc.status?.toLowerCase() ?? 'stored'}</span>}
      {fields.map(([pth, v], k) => (
        <span key={k} className="contents">
          <span className="truncate text-micro text-n7">{fieldLabel(pth)}</span>
          <span className={cx('truncate text-small text-n9 font-medium', isIdentifier(v) && 'font-mono')}>{v}</span>
        </span>
      ))}
    </>
  );
  const className = 'mt-0.5 pt-2.5 border-t border-n4 grid grid-cols-[auto_minmax(0,1fr)] items-baseline gap-x-3 gap-y-0.5 text-micro text-left text-n7 w-full';
  if (!onOpen) return <div className={className}>{body}</div>;
  return (
    <button type="button" onClick={() => onOpen(doc.ref)} aria-label={`Open the ${noun(doc.cls)}`}
      className={cx(className, 'bg-transparent border-x-0 border-b-0 p-0 pt-2.5 cursor-pointer rounded-none hover:text-n9')}>
      {body}
    </button>
  );
}

// ---------------------------------------------------------------- the bar

/** A tile of the strip: one stop, or a run of consecutive waits folded into one ("waited 46 min"). */
interface Tile { first: number; last: number; panel: Panel; label: string; at: number }

/** Consecutive "Waiting…" stops become one tile that stands for all of them; a click goes to the first. */
function tiles(panels: Panel[]): Tile[] {
  const out: Tile[] = [];
  panels.forEach((x, k) => {
    const prev = out[out.length - 1];
    if (x.tone === 'wait' && prev && prev.panel.tone === 'wait' && prev.last === k - 1) {
      prev.last = k;
      prev.label = `Waited ${duration((prev.panel.waited ?? 0) + panels.slice(prev.first + 1, k + 1).reduce((t, y) => t + (y.waited ?? 0), 0))}`;
    } else out.push({ first: k, last: k, panel: x, label: x.sticker, at: x.at });
  });
  return out;
}

/** The strip of stops: sticker and time, the current one inverted; click to jump. */
const Strip = memo(function Strip({ panels, current, onPick }: { panels: Panel[]; current: number; onPick: (k: number) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const all = useMemo(() => tiles(panels), [panels]);
  // The current stop comes to the centre at every step (the strip alone scrolls, never the page)
  useEffect(() => {
    const el = ref.current;
    const tile = el?.querySelector<HTMLElement>('[aria-current="step"]');
    if (!el || !tile) return;
    el.scrollTo({ left: tile.offsetLeft + tile.offsetWidth / 2 - el.clientWidth / 2, behavior: reducedMotion() ? 'auto' : 'smooth' });
  }, [current]);
  const edges = useEdgeFade(ref, [panels]);
  return (
    <div ref={ref} className="edge-fade flex items-stretch gap-1.5 overflow-x-auto min-w-0 py-1 pl-1.5 pr-3" onScroll={edges}>
      {all.map((x) => {
        const on = current >= x.first && current <= x.last;
        return (
          <button key={x.first} type="button" data-stop={x.first} data-tone={x.panel.tone} aria-current={on ? 'step' : undefined} aria-label={`${x.label} ${x.panel.title}`}
            onClick={() => onPick(x.first)}
            className={cx('story-stop shrink-0 min-w-[132px] h-[46px] rounded-chip px-3 flex flex-col justify-center text-left border-0 cursor-pointer transition-colors duration-[var(--t-micro)] ease-mv',
              on ? 'bg-n10 text-n1' : 'bg-n3 text-n10 hover:bg-n4', x.first > current && 'opacity-60')}>
            <span className="story-stop-s text-small font-semibold whitespace-nowrap leading-tight">{x.label}</span>
            <span className="story-stop-t font-mono text-micro tracking-wide text-n7 whitespace-nowrap tabular-nums">
              {dayWord(x.at, Date.now())} {dayjs(x.at).format('HH:mm')}
            </span>
          </button>
        );
      })}
    </div>
  );
});

const speedLabel = (s: number) => (s === 0.125 ? '⅛×' : s === 0.25 ? '¼×' : s === 0.5 ? '½×' : s < 1 ? `${s.toFixed(2).replace(/0$/, '')}×` : `${count(Math.round(s * 10) / 10)}×`);

/** How fast the story goes: ⅛× to 8× on a continuous log scale. */
function Speed({ speed, onSpeed }: { speed: number; onSpeed: (s: number) => void }) {
  const v = Math.log2(clamp(speed || 1, 0.125, 8));
  return (
    <label className="flex items-center gap-2.5">
      <span className="sr-only">Speed</span>
      <input type="range" min={-3} max={3} step={0.05} value={v} className="story-range"
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          const x = Number(e.target.value);
          const snapped = Math.abs(x - Math.round(x)) < 0.08 ? Math.round(x) : x;
          onSpeed(Math.round(2 ** snapped * 1000) / 1000);
        }} />
      <span className="font-mono text-small tabular-nums text-n10 min-w-[40px]">{speedLabel(speed)}</span>
    </label>
  );
}

/**
 * The bar under the map: what story this is and its close button, then prev · play · next, the strip, speed and
 * the clock. It sticks to the bottom of the viewport inside the stage.
 */
function Bar({ ref, title, nav, right, onClose, children }: { ref?: RefObject<HTMLDivElement | null>; title: ReactNode; nav?: ReactNode; right?: ReactNode; onClose: () => void; children: ReactNode }) {
  return (
    <div ref={ref} className="story-bar sticky bottom-4 z-[36] mt-4 mv-rise">
      <Surface level={2} radius="stage" className="flex flex-col gap-1.5 px-3 pt-2 pb-2.5 backdrop-blur-md" style={{ background: 'var(--glass)' }}>
        <div className="flex items-center gap-2.5 min-w-0 pl-1">
          {title}
          <span className="flex-1" />
          <IconButton label="Close the story" onClick={onClose}><CloseIcon size={15} /></IconButton>
        </div>
        <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3">
          <div className="flex items-center gap-1">{nav}</div>
          <div className="min-w-0">{children}</div>
          <div className="flex items-center justify-end gap-4 w-[352px] shrink-0 pl-3">{right}</div>
        </div>
      </Surface>
    </div>
  );
}
